import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type { ComponentProps, ReactElement, ReactNode } from "react";
import {
  Children,
  cloneElement,
  isValidElement,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { IconButton } from "../icon-button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../menu";

export interface BreadcrumbProps
  extends Omit<ComponentProps<"nav">, "className"> {}

function Breadcrumb(props: BreadcrumbProps) {
  return (
    <nav
      aria-label="Breadcrumb"
      data-slot="breadcrumb"
      className="w-full min-w-0"
      {...props}
    />
  );
}

export interface BreadcrumbListProps
  extends Omit<ComponentProps<"ol">, "className"> {}

function BreadcrumbList({ children, ref, ...props }: BreadcrumbListProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const measurementRef = useRef<HTMLOListElement>(null);
  const updateCollapsedStateRef = useRef(() => {});
  const [collapsed, setCollapsed] = useState(false);
  const childElements = Children.toArray(children);
  const items = childElements.filter(isBreadcrumbItem);
  const separators = childElements.filter(isBreadcrumbSeparator);
  const measurementChildren = createMeasurementChildren(children);
  const supportsOverflow =
    items.length > 2 &&
    childElements.length === items.length + separators.length;

  updateCollapsedStateRef.current = function updateCollapsedState() {
    const list = listRef.current;
    const measurement = measurementRef.current;

    if (!supportsOverflow || !list || !measurement) {
      setCollapsed(false);
      return;
    }

    const availableWidth = list.getBoundingClientRect().width;
    const requiredWidth = measurement.getBoundingClientRect().width;

    setCollapsed(availableWidth > 0 && requiredWidth > availableWidth);
  };

  function setListRef(element: HTMLOListElement | null) {
    listRef.current = element;

    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  }

  useLayoutEffect(() => {
    updateCollapsedStateRef.current();
  });

  useLayoutEffect(() => {
    const list = listRef.current;

    if (!list || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateCollapsedStateRef.current();
    });
    observer.observe(list);

    return () => {
      observer.disconnect();
    };
  }, []);

  const visibleChildren =
    collapsed && supportsOverflow
      ? [
          items[0],
          cloneSeparator(
            separators[0],
            "breadcrumb-overflow-leading-separator",
          ),
          <BreadcrumbOverflow
            key="breadcrumb-overflow"
            items={items.slice(1, -1)}
          />,
          cloneSeparator(
            separators[0],
            "breadcrumb-overflow-trailing-separator",
          ),
          items.at(-1),
        ]
      : children;

  return (
    <>
      <ol
        {...props}
        ref={setListRef}
        data-slot="breadcrumb-list"
        data-collapsed={collapsed && supportsOverflow ? "" : undefined}
        className="flex w-full min-w-0 flex-nowrap items-center gap-(--spacing-8) overflow-hidden p-(--spacing-8) text-(length:--font-size-body-md) leading-(--line-height-20)"
      >
        {visibleChildren}
      </ol>
      {supportsOverflow && (
        <ol
          ref={measurementRef}
          aria-hidden="true"
          inert
          data-slot="breadcrumb-list-measurement"
          className="pointer-events-none invisible absolute flex w-max flex-nowrap items-center gap-(--spacing-8) p-(--spacing-8) text-(length:--font-size-body-md) leading-(--line-height-20)"
        >
          {measurementChildren}
        </ol>
      )}
    </>
  );
}

export interface BreadcrumbItemProps
  extends Omit<ComponentProps<"li">, "className"> {}

function BreadcrumbItem(props: BreadcrumbItemProps) {
  return (
    <li
      data-slot="breadcrumb-item"
      className="inline-flex min-w-0 items-center"
      {...props}
    />
  );
}

export interface BreadcrumbLinkProps
  extends Omit<useRender.ComponentProps<"a">, "className"> {}

function BreadcrumbLink({ render, ...props }: BreadcrumbLinkProps) {
  return useRender({
    defaultTagName: "a",
    render,
    props: mergeProps<"a">(props, {
      className: [
        "text-breadcrumb-text-inactive",
        "font-(family-name:--font-family-body)",
        "font-(number:--font-weight-regular)",
        "no-underline",
        "block",
        "min-w-0",
        "max-w-full",
        "truncate",
        "whitespace-nowrap",
        "cursor-pointer",
        "transition-[color,text-decoration-color]",
        // Hover
        "hover:underline",
        "hover:[text-decoration-skip-ink:none]",
        // Pressed
        "active:text-breadcrumb-text-inactive-pressed",
        "active:underline",
        "active:[text-decoration-skip-ink:none]",
        "active:[text-decoration-style:solid]",
        // Focus
        "outline-none",
        "focus-visible:[outline:var(--spacing-2)_solid_var(--color-breadcrumb-focus-ring)]",
        "focus-visible:outline-offset-[3px]",
        "rounded-(--radius-sm)",
      ].join(" "),
    }),
  });
}

export interface BreadcrumbPageProps
  extends Omit<ComponentProps<"span">, "className"> {}

function BreadcrumbPage(props: BreadcrumbPageProps) {
  return (
    <span
      data-slot="breadcrumb-page"
      aria-current="page"
      className="block min-w-0 max-w-full truncate text-breadcrumb-text-active font-family-body font-semibold"
      {...props}
    />
  );
}

function isBreadcrumbItem(
  child: ReactNode,
): child is ReactElement<BreadcrumbItemProps> {
  return isValidElement(child) && child.type === BreadcrumbItem;
}

function isBreadcrumbSeparator(
  child: ReactNode,
): child is ReactElement<BreadcrumbSeparatorProps> {
  return isValidElement(child) && child.type === BreadcrumbSeparator;
}

function isBreadcrumbLink(
  child: ReactNode,
): child is ReactElement<BreadcrumbLinkProps> {
  return isValidElement(child) && child.type === BreadcrumbLink;
}

function cloneSeparator(
  separator: ReactElement<BreadcrumbSeparatorProps> | undefined,
  key: string,
) {
  return separator ? (
    cloneElement(separator, { key })
  ) : (
    <BreadcrumbSeparator key={key} />
  );
}

function createMeasurementChildren(children: ReactNode) {
  return Children.map(children, (child) => {
    if (!isBreadcrumbItem(child) || !isBreadcrumbLink(child.props.children)) {
      return child;
    }

    return cloneElement(child, {
      children: cloneElement(child.props.children, { tabIndex: -1 }),
    });
  });
}

function BreadcrumbOverflow({
  items,
}: {
  items: ReactElement<BreadcrumbItemProps>[];
}) {
  const [open, setOpen] = useState(false);
  const hiddenItemCount = items.length;

  return (
    <li data-slot="breadcrumb-overflow" className="inline-flex shrink-0">
      <Menu modal={false} open={open} onOpenChange={setOpen}>
        <MenuTrigger
          render={
            <IconButton
              aria-label={`Show ${hiddenItemCount} hidden breadcrumb ${hiddenItemCount === 1 ? "level" : "levels"}`}
              icon="ellipsis-dots-horiz"
              size="sm"
              variant="ghost"
            />
          }
        />
        <MenuPopup align="start" size="sm">
          {items.map((item, index) => (
            <BreadcrumbOverflowMenuItem key={item.key ?? index} item={item} />
          ))}
        </MenuPopup>
      </Menu>
    </li>
  );
}

function BreadcrumbOverflowMenuItem({
  item,
}: {
  item: ReactElement<BreadcrumbItemProps>;
}) {
  const itemContent = item.props.children;

  if (isBreadcrumbLink(itemContent)) {
    return (
      <MenuItem render={itemContent}>{itemContent.props.children}</MenuItem>
    );
  }

  return <MenuItem disabled>{itemContent}</MenuItem>;
}

export interface BreadcrumbSeparatorProps
  extends Omit<ComponentProps<"li">, "className" | "children"> {}

function BreadcrumbSeparator(props: BreadcrumbSeparatorProps) {
  return (
    <li
      data-slot="breadcrumb-separator"
      aria-hidden="true"
      className="text-breadcrumb-separator font-family-label font-medium shrink-0 select-none"
      {...props}
    >
      {/* "/" is the only supported separator per the design spec */}/
    </li>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
};
